import "antd/dist/reset.css";
import { Link } from "react-router-dom";
import { PageHeader } from "../../CommonElements/PageTitle";
import { AddTeamIcon, LockIcon } from "../../assets/icons";
import { Col, Input, Row } from "reactstrap";
import { Container, Stack } from "react-bootstrap";
import { Button } from "reactstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  PencilEdit02Icon,
  SortByDown02Icon,
  Delete01Icon,
  Cancel01Icon,
} from "@hugeicons/core-free-icons";
import {
  useDeleteGroupMutation,
  useGetOneGroupQuery,
  useGroupsQuery,
  useSelectedGroupPermissionsQuery,
} from "../../services/groups";
import { useMemo, useState } from "react";
import PermissionDisplay from "./CreateGroup/components/PermissionDisplay";
import ActionDropdown from "../../CommonElements/ActionDropdown";
import { useTranslation } from "react-i18next";
import { useLocalizedText } from "../../utils/hooks/useLocalizedText";
import ConfirmModal from "../../CommonElements/ConfirmModal";

import { usePermissions } from "../../utils/hooks/usePermissions";
import BtnAsLink from "../../Component/BtnAsLink";
import { PLATFORMS, USER_MANAGEMENT_PERMISSIONS_FULL_NAMES } from "@/utils/constants";
import { Flex } from "antd";
import { SystemsDropDown } from "@/CommonElements/SystemsDropDown/Index";
import { Can } from "@/CommonElements/PermissionGate";

const EMPTY_GROUP_PERMISSIONS = [];
const EMPTY_GROUPS = [];
const Groups = () => {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(PLATFORMS.makkah.key);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const { hasPermissionByName } = usePermissions();

  const SystemId = PLATFORMS[selectedSystem].system_id;

  const {
    data: groupsRes,
    isLoading,
    isFetching,
  } = useGroupsQuery({ system_id: SystemId });
  const groups = useMemo(
    () => groupsRes?.data?.data ?? EMPTY_GROUPS,
    [groupsRes]
  );
  // Search and sort states
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [groupSortOrder, setGroupSortOrder] = useState("asc"); // 'asc' or 'desc'
  const [permissionSearchTerm, setPermissionSearchTerm] = useState("");

  const { t } = useTranslation();

  const loading = isLoading || isFetching;

  const { getLocalizedText } = useLocalizedText();

  // Filter and sort groups
  const filteredAndSortedGroups = useMemo(() => {
    let filtered = groups.filter((group) =>
      group.name.toLowerCase().includes(groupSearchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const comparison = a.name.localeCompare(b.name, "ar", { numeric: true });
      return groupSortOrder === "asc" ? comparison : -comparison;
    });
  }, [groups, groupSearchTerm, groupSortOrder]);

  // Handle group select
  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    // Clear permission search when selecting a new group
    setPermissionSearchTerm("");
  };

  const { mutate: deleteGroup, isPending: isDeletFroupPending } =
    useDeleteGroupMutation();

  const handleDeleteGroup = (groupId) => {
    setGroupToDelete(groupId);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (groupToDelete) {
      deleteGroup({ id: groupToDelete });
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setGroupToDelete(null);
  };

  const canUpdate = hasPermissionByName(USER_MANAGEMENT_PERMISSIONS_FULL_NAMES.update_group_for_user_management);
  const canDelete = hasPermissionByName(USER_MANAGEMENT_PERMISSIONS_FULL_NAMES.delete_group_for_user_management);

  const items = (groupId) => [
    ...(canUpdate
      ? [
          {
            label: (
            <Link
              to={`${groupId}/edit`}
              className="d-flex align-items-center gap-2"
            >
              <HugeiconsIcon
                size={18}
                icon={PencilEdit02Icon}
                className="flex-shrink-0"
              />
                <span>{t("EDIT")}</span>
              </Link>
            ),
            key: "edit",
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            label: (
              <button
                type="button"
                className="d-flex align-items-center gap-2 btn btn-link p-0 text-danger text-decoration-none"
                onClick={() => handleDeleteGroup(groupId)}
              >
              <HugeiconsIcon
                size={18}
                icon={Delete01Icon}
                className="flex-shrink-0"
              />
                <span>{t("DELETE")}</span>
              </button>
            ),
            key: "delete",
            danger: true,
          },
        ]
      : []),
  ];

  const {
    data: selectedGroupPermissionsRes,
    isLoading: isSelectedGroupPermissionsLoading,
    isFetching: isSelectedGroupPermissionsFetching,
  } = useSelectedGroupPermissionsQuery(selectedGroup?.id);
  const { data: selectedGroupDetailsRes } = useGetOneGroupQuery(selectedGroup?.id);
  const selectedGroupPermissions = useMemo(
    () => selectedGroupPermissionsRes?.data?.data ?? EMPTY_GROUP_PERMISSIONS,
    [selectedGroupPermissionsRes],
  );
  const selectedPermissionIds = useMemo(
    () =>
      new Set(
        selectedGroupDetailsRes?.data?.data?.permissions?.map((permission) => permission?.id) ?? [],
      ),
    [selectedGroupDetailsRes],
  );

  const selectedGroupPermissionsLoading = isSelectedGroupPermissionsLoading || isSelectedGroupPermissionsFetching;

  const filteredPermissions = useMemo(() => {
    if (!selectedGroupPermissions?.length || !permissionSearchTerm) {
      return selectedGroupPermissions;
    }

    const searchTerm = permissionSearchTerm.toLowerCase().trim();

    const filterNode = (node) => {
      const filteredNode = { ...node };

      // Filter permissions in current node
      if (node.permissions) {
        filteredNode.permissions = node.permissions.filter((permission) => {
          // Search in all possible permission text fields
          const permissionName = permission.name || "";
          const displayNameAr = permission.display_name_ar || "";
          const displayNameEn = permission.display_name_en || "";
          const localizedDisplayName = getLocalizedText(displayNameAr, displayNameEn) || "";

          return (
            permissionName.toLowerCase().includes(searchTerm) ||
            displayNameAr.toLowerCase().includes(searchTerm) ||
            displayNameEn.toLowerCase().includes(searchTerm) ||
            localizedDisplayName.toLowerCase().includes(searchTerm)
          );
        });
      }

      // Filter children recursively
      if (node.children) {
        filteredNode.children = node.children
          .map(filterNode)
          .filter((child) => {
            const hasPermissions =
              child.permissions && child.permissions.length > 0;
          const hasChildren = child.children && child.children.length > 0;

          // Search in node names (categories)
          const childName = child.name || "";
          const childNameAr = child.name_ar || "";
          const childNameEn = child.name_en || "";
            const localizedChildName =
              getLocalizedText(childNameAr, childNameEn) || childName;

          const nameMatches =
            childName.toLowerCase().includes(searchTerm) ||
            childNameAr.toLowerCase().includes(searchTerm) ||
            childNameEn.toLowerCase().includes(searchTerm) ||
            localizedChildName.toLowerCase().includes(searchTerm);

          return hasPermissions || hasChildren || nameMatches;
        });
      }

      return filteredNode;
    };

    return selectedGroupPermissions.map(filterNode).filter((node) => {
      const hasPermissions = node.permissions && node.permissions.length > 0;
      const hasChildren = node.children && node.children.length > 0;

      // Search in root node names
      const nodeName = node.name || "";
      const nodeNameAr = node.name_ar || "";
      const nodeNameEn = node.name_en || "";
      const localizedNodeName =
        getLocalizedText(nodeNameAr, nodeNameEn) || nodeName;

      const nameMatches =
        nodeName.toLowerCase().includes(searchTerm) ||
        nodeNameAr.toLowerCase().includes(searchTerm) ||
        nodeNameEn.toLowerCase().includes(searchTerm) ||
        localizedNodeName.toLowerCase().includes(searchTerm);

      return hasPermissions || hasChildren || nameMatches;
    });
  }, [selectedGroupPermissions, permissionSearchTerm, getLocalizedText]);


  return (
    <div>
      <PageHeader title={t("GROUPS")} sticky={false}>
        <Can
          permission={USER_MANAGEMENT_PERMISSIONS_FULL_NAMES.create_group_for_user_management}
          fallback={null}
        >
          <BtnAsLink
            className="btn-primary"
            link="/app/user_management/groups/create"
            icon={<AddTeamIcon />}
          >
            {t("ADD_NEW_GROUP")}
          </BtnAsLink>
        </Can>
      </PageHeader>

      <Container fluid>
        <Row className="mt-3">
          <Col lg={6} xl={4}>
            <div className="card border border-1 groups-list-card">
              <div className="card-body p-3">
                <Flex className="mb-3" align="center" justify="space-between">
                  <h3>المجموعات</h3>

                  <SystemsDropDown
                    value={selectedSystem}
                    onChange={(key) => {
                      setSelectedSystem(key);
                    }}
                  />

                  {/* <Link
                      to={System?.path}
                      className="d-flex align-items-center gap-2 platform-select-trigger"
                    >
                      {t(System?.name) || ""} {SystemIcon}
                    </Link> */}
                </Flex>

                {loading ? (
                  <>
                    <Stack direction="horizontal" gap={2}>
                      <div className="skeleton skeleton-input"></div>
                      <div className="skeleton skeleton-btn-icon"></div>
                    </Stack>

                    <ul className="skeleton-list">
                      <li>
                        <div className="skeleton skeleton-list-item"></div>
                      </li>
                      <li>
                        <div className="skeleton skeleton-list-item"></div>
                      </li>
                      <li>
                        <div className="skeleton skeleton-list-item"></div>
                      </li>
                      <li>
                        <div className="skeleton skeleton-list-item"></div>
                      </li>
                      <li>
                        <div className="skeleton skeleton-list-item"></div>
                      </li>
                    </ul>
                  </>
                ) : (
                  <>
                    <Stack direction="horizontal" gap={2}>
                      <Input
                        type="text"
                        placeholder="ابحث عن مجموعة"
                        value={groupSearchTerm}
                        onChange={(e) => setGroupSearchTerm(e.target.value)}
                      />
                      <Button
                        variant="secondary"
                        className="outline btn-icon btn-xs"
                        onClick={() =>
                          setGroupSortOrder((prev) =>
                            prev === "asc" ? "desc" : "asc"
                          )
                        }
                        title={
                          groupSortOrder === "asc"
                            ? "ترتيب تنازلي"
                            : "ترتيب تصاعدي"
                        }
                      >
                        <HugeiconsIcon
                          icon={SortByDown02Icon}
                          style={{
                            transform:
                              groupSortOrder === "desc"
                                ? "rotate(180deg)"
                                : "none",
                          }}
                        />
                      </Button>
                    </Stack>

                    {groups.length === 0 ? (
                      <div className="empty-state text-center">
                        <div className="empty-state-icon">
                          <AddTeamIcon
                            style={{ width: "40px", height: "40px" }}
                          />
                        </div>
                        <h4 className="empty-state-title">
                          {t("NO_GROUPS_FOUND")}
                        </h4>
                        <p className="empty-state-description">
                          {t("NO_GROUPS_DESCRIPTION")}
                        </p>
                        <Link
                          to="/app/user_management/groups/create"
                          className="btn btn-primary"
                        >
                          <AddTeamIcon />
                          <span>{t("CREATE_FIRST_GROUP")}</span>
                        </Link>
                      </div>
                    ) : (
                      <ul className="groups-list">
                        {filteredAndSortedGroups.map((group) => (
                          <li key={group?.id}>
                            <Stack direction="horizontal" gap={2}>
                              <button
                                className={`btn btn-sm w-100 justify-content-start group-item ${selectedGroup?.id === group?.id
                                  ? "selected"
                                  : ""
                                }`}
                                onClick={() => handleGroupSelect(group)}
                              >
                                {group.name}
                              </button>
                              <Can anyOf={[USER_MANAGEMENT_PERMISSIONS_FULL_NAMES.update_group_for_user_management, USER_MANAGEMENT_PERMISSIONS_FULL_NAMES.delete_group_for_user_management]}>

                                <ActionDropdown
                                  items={items(group?.id)}
                                  buttonClassName="outline btn-icon btn-xs"
                                />
                              </Can>
                            </Stack>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>
          </Col>
          <Col>
            <div className="card border border-1">
              <div className="card-body p-3">
                {selectedGroup ? (
                  <>
                    <h3 className="mb-3">
                      صلاحيات مجموعة: {selectedGroup.name}{" "}
                    </h3>

                    {selectedGroupPermissionsLoading ? (
                      <div className="skeleton skeleton-input mb-3"></div>
                    ) : (
                      <Input
                        type="text"
                        placeholder="ابحث عن صلاحية"
                        className="mb-3"
                        value={permissionSearchTerm}
                        onChange={(e) =>
                          setPermissionSearchTerm(e.target.value)
                        }
                      />
                    )}

                    <div>
                      <PermissionDisplay
                        permissionsData={filteredPermissions}
                        selectedPermissions={selectedPermissionIds}
                        loading={selectedGroupPermissionsLoading}
                        col={12}
                      />
                    </div>
                  </>
                ) : (
                  <div className="permissions-empty-state text-center">
                    <div className="permissions-empty-state-icon">
                      <LockIcon size={48} />
                    </div>
                    <h4 className="permissions-empty-state-title">
                      {t("NO_GROUP_SELECTED")}
                    </h4>
                    <p className="permissions-empty-state-description">
                      {t("SELECT_GROUP_DESCRIPTION")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <ConfirmModal
        isOpen={showDeleteModal}
        onToggle={cancelDelete}
        onConfirm={confirmDelete}
        title={t("CONFIRM")}
        message={t("CONFIRM_DELETE_GROUP")}
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeletFroupPending}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={Delete01Icon}
        cancelIcon={Cancel01Icon}
      />
    </div>
  );
};

export default Groups;
